"use client";

import type * as React from "react";

import { cn } from "../cn";
import { CountPill } from "./count-pill";
import { Tabs, TabsList, TabsTrigger } from "./tabs";
import { ToggleGroup, ToggleGroupItem } from "./toggle-group";

// Design spec §6.2: underline view tabs and the filter-chip `Segmented` control.

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type ViewTab = {
  value: string;
  label: React.ReactNode;
  icon?: IconType;
  count?: number;
  /** A small mark after the label inside the tab (e.g. "default"). */
  marker?: React.ReactNode;
  /**
   * A control beside the tab (its own menu trigger, never inside the tab button). It sits in a
   * `group/view-tab` wrapper, so it can show on the tab's hover or focus.
   */
  menu?: React.ReactNode;
};

export type ViewTabsProps = {
  tabs: ViewTab[];
  value: string;
  onValueChange: (value: string) => void;
  "aria-label": string;
  /** `TabsContent` panels, when the tabs own their panels (URL-driven views may render none). */
  children?: React.ReactNode;
  className?: string;
};

/**
 * 40 px underline tabs on a --border rule: 13 / 500 muted, the selected one --foreground with a
 * 2 px --primary bar; optional 16 px icon and count. Scrolls sideways, never wraps.
 */
export function ViewTabs({
  tabs,
  value,
  onValueChange,
  children,
  className,
  "aria-label": ariaLabel,
}: ViewTabsProps) {
  return (
    <Tabs
      data-slot="view-tabs"
      value={value}
      onValueChange={(next) => onValueChange(String(next))}
      className={cn("gap-0", className)}
    >
      <TabsList variant="line" aria-label={ariaLabel} className="shrink-0 px-0">
        {tabs.map(({ value: v, label, icon: Icon, count, marker, menu }) => {
          const trigger = (
            <TabsTrigger key={v} value={v} className="gap-1.5">
              {Icon ? <Icon aria-hidden={true} /> : null}
              {label}
              {count !== undefined ? (
                <CountPill count={count} tone={v === value ? "accent" : "neutral"} />
              ) : null}
              {marker}
            </TabsTrigger>
          );
          if (!menu) return trigger;
          return (
            <span
              key={v}
              data-slot="view-tab"
              className="group/view-tab inline-flex h-full shrink-0 items-center gap-0.5"
            >
              {trigger}
              {menu}
            </span>
          );
        })}
      </TabsList>
      {children}
    </Tabs>
  );
}

export type SegmentedOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  icon?: IconType;
};

export type SegmentedProps<T extends string> = {
  options: SegmentedOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  size?: "sm" | "md";
  "aria-label": string;
  className?: string;
};

/**
 * DTP filter tabs: 12 / 500 chips, 32 px (28 for `sm`), radius 6; the selected chip is cobalt
 * (--primary-soft, a --primary hairline). One value is always selected (`aria-pressed`).
 */
export function Segmented<T extends string>({
  options,
  value,
  onValueChange,
  size = "md",
  className,
  "aria-label": ariaLabel,
}: SegmentedProps<T>) {
  return (
    <ToggleGroup
      data-slot="segmented"
      variant="chip"
      size={size === "sm" ? "sm" : "default"}
      spacing={1}
      aria-label={ariaLabel}
      value={[value]}
      onValueChange={(next: unknown[]) => {
        const picked = next.find((v) => v !== value) ?? next[0];
        if (picked !== undefined) onValueChange(picked as T);
      }}
      className={className}
    >
      {options.map(({ value: v, label, icon: Icon }) => (
        <ToggleGroupItem key={v} value={v}>
          {Icon ? <Icon aria-hidden={true} /> : null}
          {label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
